import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, Loader2, Check, Upload, FileText, Trash2, ImageIcon, Pencil } from "lucide-react";
import { API } from "../data/site";

const linesToList = (text) => text.split("\n").map((s) => s.trim()).filter(Boolean);
const pairsToSpecs = (text) =>
  linesToList(text)
    .map((line) => {
      const [k, ...rest] = line.split("|");
      return { k: (k || "").trim(), v: rest.join("|").trim() };
    })
    .filter((s) => s.k && s.v);

const inputCls = "w-full border border-slate-300 bg-white px-3 py-2 text-sm text-navy focus:border-safety focus:outline-none";

const ProductEditor = ({ product, onDone }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: product.name,
    short: product.short || "",
    blurb: product.blurb || "",
    rangeLabel: product.rangeLabel || "",
    voltages: (product.voltages || []).join(", "),
    types: (product.types || []).join(", "),
    specs: (product.specs || []).map((s) => `${s.k} | ${s.v}`).join("\n"),
    applications: (product.applications || []).join("\n"),
  });
  const [dsLabel, setDsLabel] = useState("");
  const [dsFile, setDsFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saved, setSaved] = useState(false);
  const photoRef = useRef(null);
  const dsRef = useRef(null);

  useEffect(() => {
    setForm({
      name: product.name, short: product.short || "", blurb: product.blurb || "",
      rangeLabel: product.rangeLabel || "",
      voltages: (product.voltages || []).join(", "), types: (product.types || []).join(", "),
      specs: (product.specs || []).map((s) => `${s.k} | ${s.v}`).join("\n"),
      applications: (product.applications || []).join("\n"),
    });
  }, [product.slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name, short: form.short, blurb: form.blurb, rangeLabel: form.rangeLabel,
        voltages: linesToList(form.voltages.replace(/,\s*/g, ",").split(",").length ? form.voltages.split(",").map((s) => s.trim()).filter(Boolean) : []),
        types: form.types.split(",").map((s) => s.trim()).filter(Boolean),
        specs: pairsToSpecs(form.specs),
        applications: linesToList(form.applications),
      };
      const res = await fetch(`${API}/admin/products/${product.slug}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      return res.json();
    },
    onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2500); qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
  });

  const uploadPhoto = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", photoFile);
      const res = await fetch(`${API}/admin/products/${product.slug}/photo`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      return res.json();
    },
    onSuccess: () => { setPhotoFile(null); if (photoRef.current) photoRef.current.value = ""; qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
  });

  const uploadDs = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", dsFile);
      fd.append("label", dsLabel);
      const res = await fetch(`${API}/admin/products/${product.slug}/datasheet`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      return res.json();
    },
    onSuccess: () => { setDsFile(null); setDsLabel(""); if (dsRef.current) dsRef.current.value = ""; qc.invalidateQueries({ queryKey: ["admin-products"] }); },
  });

  const deleteDs = useMutation({
    mutationFn: async (dsId) => {
      const res = await fetch(`${API}/admin/products/${product.slug}/datasheet/${dsId}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Delete failed");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  return (
    <div className="border border-slate-200 bg-white p-6 sm:p-8" data-testid={`product-editor-${product.slug}`}>
      <div className="flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2.5 font-display text-lg font-extrabold text-navy">
          <Pencil size={16} className="text-safety" /> Edit — {product.name}
        </h2>
        <button onClick={onDone} data-testid="product-editor-close" className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 hover:text-safety">Close</button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[300px_1fr]">
        <div>
          <div className="relative overflow-hidden border border-slate-200 bg-navy" data-testid="product-photo-preview">
            <img src={product.image} alt={product.name} className="aspect-[4/3] w-full object-cover" />
          </div>
          <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-slate-300 bg-slate-50 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-safety hover:text-safety" data-testid={`product-photo-label-${product.slug}`}>
            <ImageIcon size={14} className="text-safety" /> Replace photo
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" ref={photoRef}
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid={`product-photo-input-${product.slug}`} />
          </label>
          {photoFile && (
            <button
              onClick={() => uploadPhoto.mutate()}
              disabled={uploadPhoto.isPending}
              data-testid={`product-photo-upload-${product.slug}`}
              className="mt-2 flex w-full items-center justify-center gap-2 bg-safety px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white disabled:opacity-60"
            >
              {uploadPhoto.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload photo
            </button>
          )}
          {uploadPhoto.isError && <p className="mt-2 text-xs text-red-600">{uploadPhoto.error.message}</p>}

          <div className="mt-6 border-t border-slate-200 pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-safety">Datasheets</p>
            <ul className="mt-3 space-y-2" data-testid={`datasheet-list-${product.slug}`}>
              {(product.datasheets || []).map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 border border-slate-200 px-3 py-2">
                  <a href={d.file} target="_blank" rel="noopener noreferrer" className="flex min-w-0 items-center gap-2 text-xs text-navy hover:text-safety">
                    <FileText size={13} className="shrink-0 text-safety" /> <span className="truncate">{d.label}</span>
                  </a>
                  <button
                    onClick={() => deleteDs.mutate(d.id)}
                    data-testid={`datasheet-delete-${d.id}`}
                    className="shrink-0 text-slate-400 transition-colors hover:text-red-600"
                    title="Remove datasheet"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
              {!(product.datasheets || []).length && <li className="text-xs text-slate-400">No datasheets yet.</li>}
            </ul>
            <div className="mt-3 space-y-2">
              <input value={dsLabel} onChange={(e) => setDsLabel(e.target.value)} placeholder="Datasheet label (optional)" data-testid={`datasheet-label-input-${product.slug}`} className={inputCls} />
              <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-navy hover:border-safety hover:text-safety">
                <FileText size={13} className="text-safety" /> Choose PDF
                <input type="file" accept="application/pdf" className="hidden" ref={dsRef}
                  onChange={(e) => setDsFile(e.target.files?.[0] || null)} data-testid={`datasheet-file-input-${product.slug}`} />
              </label>
              {dsFile && (
                <button
                  onClick={() => uploadDs.mutate()}
                  disabled={uploadDs.isPending}
                  data-testid={`datasheet-upload-${product.slug}`}
                  className="flex w-full items-center justify-center gap-2 bg-navy px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white disabled:opacity-60"
                >
                  {uploadDs.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload datasheet
                </button>
              )}
              {uploadDs.isError && <p className="text-xs text-red-600">{uploadDs.error.message}</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Name</span>
              <input value={form.name} onChange={set("name")} data-testid={`edit-name-${product.slug}`} className={`${inputCls} mt-1`} /></label>
            <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Voltage range label</span>
              <input value={form.rangeLabel} onChange={set("rangeLabel")} data-testid={`edit-range-${product.slug}`} className={`${inputCls} mt-1`} /></label>
          </div>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Short line</span>
            <textarea rows={2} value={form.short} onChange={set("short")} data-testid={`edit-short-${product.slug}`} className={`${inputCls} mt-1`} /></label>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Blurb</span>
            <textarea rows={4} value={form.blurb} onChange={set("blurb")} data-testid={`edit-blurb-${product.slug}`} className={`${inputCls} mt-1`} /></label>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Voltage chips (comma separated)</span>
            <input value={form.voltages} onChange={set("voltages")} data-testid={`edit-voltages-${product.slug}`} className={`${inputCls} mt-1`} /></label>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Types (comma separated)</span>
            <input value={form.types} onChange={set("types")} data-testid={`edit-types-${product.slug}`} className={`${inputCls} mt-1`} /></label>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Specs (one per line — Key | Value)</span>
            <textarea rows={4} value={form.specs} onChange={set("specs")} data-testid={`edit-specs-${product.slug}`} className={`${inputCls} mt-1 font-mono text-xs`} /></label>
          <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Applications (one per line)</span>
            <textarea rows={4} value={form.applications} onChange={set("applications")} data-testid={`edit-applications-${product.slug}`} className={`${inputCls} mt-1`} /></label>

          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            data-testid={`save-product-${product.slug}`}
            className="flex items-center justify-center gap-2.5 bg-safety px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy disabled:opacity-60"
          >
            {save.isPending ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : saved ? <><Check size={15} /> Saved</> : "Save changes"}
          </button>
          {save.isError && <p className="text-xs text-red-600">{save.error.message}</p>}
        </div>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/products`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });
  const products = data?.products || [];

  return (
    <div data-testid="admin-products-page">
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-navy">Products</h1>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
        Edits go live on the website immediately
      </p>

      {isLoading ? (
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Loading products…</p>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-products-list">
          {products.map((p) => (
            <div key={p.slug} className="border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-4 p-4">
                <div className="flex min-w-0 items-center gap-4">
                  <img src={p.image} alt="" className="h-14 w-20 shrink-0 object-cover" />
                  <div className="min-w-0">
                    <p className="truncate font-display text-sm font-bold text-navy">{p.name}</p>
                    <p className="truncate font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">{p.rangeLabel} · {p.datasheets?.length || 0} datasheets</p>
                  </div>
                </div>
                <button
                  onClick={() => setEditing(editing === p.slug ? null : p.slug)}
                  data-testid={`admin-edit-button-${p.slug}`}
                  className="flex shrink-0 items-center gap-2 border border-slate-300 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-safety hover:text-safety"
                >
                  <Pencil size={12} /> {editing === p.slug ? "Close" : "Edit"}
                </button>
              </div>
              {editing === p.slug && <div className="border-t border-slate-200 p-4 sm:p-6"><ProductEditor product={p} onDone={() => setEditing(null)} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
